import React, {useEffect} from 'react'
import {useTranslation} from 'react-i18next'
import {RouteComponentProps} from 'react-router'
import {useHistory, useParams} from 'react-router-dom'

import {Box, Typography} from '@mui/material'
import HTMLReactParser from 'html-react-parser'
import {useSnackbar} from 'notistack'

import {ReactComponent as CheckGraphicSVG} from '@progyny/assets/icons/big-check.svg'
import SubmitButton from '@progyny/components/Buttons/SubmitButton'
import PageLoader from '@progyny/components/PageLoader'
import SimpleMessageContainer from '@progyny/components/SimpleMessageContainer'
import {
  MeDocument,
  useConfirmPrivateEmailMutation,
} from '@progyny/generated/graphql'
import {useErrorCodes} from '@progyny/hooks/useErrorCodes'
import {RouteEnum, useRoutes} from '@progyny/routes'
import {useMeStore} from '@progyny/store/meStore'

import {theme} from '@root/theme'

const ValidatePrivateEmail: React.FC<RouteComponentProps> = (props) => {
  const {t} = useTranslation()
  const {match} = props

  const {token} = useParams() as {token: string}
  const {enqueueSnackbar} = useSnackbar()
  const getErrorMessage = useErrorCodes()
  const history = useHistory()

  const [confirmPrivateEmail, {loading}] = useConfirmPrivateEmailMutation()
  const user = useMeStore((s) => s.user)
  const isAuthorized = useMeStore((s) => s.isAuthorized)
  const privateEmail = user?.member?.privateEmail as string
  const {goTo, getPath} = useRoutes()

  useEffect(() => {
    if (token) {
      confirmPrivateEmail({
        variables: {token},
        refetchQueries: [{query: MeDocument}],
        awaitRefetchQueries: true,
      })
        .then((result) => {
          const data = result.data!.confirmPrivateEmail
          const success = Boolean(data?.success)
          const errorCode = data?.errorCode as string

          if (!success) {
            if (errorCode === 'user-not-logged-in') {
              const next = encodeURI(match.url)
              history.push(
                `${getPath(RouteEnum.LOGIN)}?next=${next}&show-login=1`
              )
            } else {
              enqueueSnackbar({
                message: getErrorMessage(errorCode),
                variant: 'error',
              })
              // Let top level router figure out where to go from here
              history.push(getPath(RouteEnum.EMAIL_TOKEN_EXPIRED))
            }
          }
        })
        .catch((reason) => {
          enqueueSnackbar({
            message: t('validate_private_email_errors_unable_to_validate_your'),
            variant: 'error',
          })
          // Let top level router figure out where to go from here
          history.push('/')
        })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, confirmPrivateEmail])

  return (
    <>
      {loading ? (
        <PageLoader position={'center'} />
      ) : (
        <SimpleMessageContainer
          CustomImage={
            <Box
              sx={{
                background: theme.palette.neutral[50],
                borderRadius: '50%',
                height: 100,
                width: 100,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <CheckGraphicSVG style={{width: 50, height: 50}} />
            </Box>
          }
          actionArea={[
            <SubmitButton onClick={() => goTo(RouteEnum.HOME)}>
              {isAuthorized && user?.member?.privateEmailConfirmed
                ? t('validate_private_email_go_to_startpage')
                : t('validate_private_email_continue_onboarding')}
            </SubmitButton>,
          ]}
          message={
            <Box
              mt={3}
              display={'flex'}
              alignItems={'center'}
              flexDirection={'column'}
            >
              <Typography variant={'h1'}>
                {t('validate_email_thank_you')}
              </Typography>
              <Box mt={2}>
                <Typography variant='body1' textAlign={'center'} width={525}>
                  {HTMLReactParser(
                    t('validate_private_email_your_private_email_address_has', {
                      privateEmail,
                    })
                  )}
                </Typography>
              </Box>
            </Box>
          }
        />
      )}
    </>
  )
}
export default ValidatePrivateEmail
